import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { View, Animated, StyleSheet, Pressable, Text } from "react-native";

import CustomText from "../../common/general/CustomText/CustomText";
import { SafetyCase, Severity } from "../../../graphql/generated/graphql";
import { getInitials } from "../../../utils/getInitials";

import { colors, spacing } from "../../../styles/design-system";
import { ShieldAlert } from "lucide-react";
import { DotsThreeVerticalIcon } from "@phosphor-icons/react";

export enum SafetyPanelState {
  CLOSED = "CLOSED",
  OPEN = "OPEN",
}

enum SafetyTab {
  DETAILS = "DETAILS",
  ACTIVITY = "ACTIVITY",
}

interface SafetyCasePanelProps {
  panelState: SafetyPanelState;
  setPanelState: (state: SafetyPanelState) => void;
  selectedCase: SafetyCase | null;
}

const formatDate = (dateString?: string | null) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

export const SafetyCasePanel: React.FC<SafetyCasePanelProps> = ({ panelState, setPanelState, selectedCase }) => {
  const [activeTab, setActiveTab] = useState<SafetyTab>(SafetyTab.DETAILS);
  const [slideAnim] = useState(new Animated.Value(0));

  const handleClose = () => {
    setPanelState(SafetyPanelState.CLOSED);
  };

  useEffect(() => {
    Animated.timing(slideAnim, {
      toValue: panelState === SafetyPanelState.OPEN ? 1 : 0,
      duration: 300,
      useNativeDriver: false,
    }).start();
  }, [panelState]);

  const panelWidth = 800;

  const translateX = slideAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [panelWidth, 0],
  });

  if (panelState === SafetyPanelState.CLOSED || !selectedCase) return null;

  const getSeverityColor = (severity: Severity) => {
    switch (severity) {
      case Severity.Critical:
        return colors.error[700];

      case Severity.High:
        return colors.warning[700];

      case Severity.Medium:
        return colors.warning[600];

      case Severity.Low:
        return colors.brand[600];

      default:
        return colors.grey[600];
    }
  };

  const severityColor = getSeverityColor(selectedCase.severity);

  return createPortal(
    <>
      {/* Overlay */}
      <Pressable style={styles.overlay} onPress={handleClose}>
        <Animated.View style={{ opacity: slideAnim, flex: 1 }} />
      </Pressable>

      {/* Panel */}
      <Animated.View
        style={[styles.panel, { transform: [{ translateX }] }]}
        onStartShouldSetResponder={() => true} // prevent overlay close
      >
        {/* Header */}

        <View style={styles.caseHeader}>
          {/* Top Row */}
          <View style={styles.caseHeaderTopRow}>
            <View>
              <View style={styles.caseMetaRow}>
                <CustomText
                  useTranslationText={false}
                  text={`Case #${selectedCase.messageId}`}
                  size="xs"
                  style={styles.caseId}
                />
                <ShieldAlert size={16} color={colors.error[600]} />
              </View>

              <CustomText
                text={selectedCase.categories.join(", ") || "Safety Case"}
                size="l"
                weight="semibold"
                style={styles.caseTitle}
                useTranslationText={false}
              />
            </View>

            {/* Ellipsis Menu */}
            <Pressable style={styles.moreButton}>
              <DotsThreeVerticalIcon size={20} color={colors.grey[500]} />
            </Pressable>
          </View>

          {/* Severity + Status Row */}
          <View style={styles.caseActionsRow}>
            <View
              style={[
                styles.severityBadge,
                {
                  backgroundColor: `${severityColor}20`,
                  borderColor: severityColor,
                },
              ]}>
              <CustomText
                useTranslationText={false}
                text={selectedCase.severity}
                size="xs"
                weight="medium"
                style={{ color: severityColor }}
              />
            </View>

            {/* Do not remove it will be implemented in the next PR */}
            {/* <Pressable style={styles.statusButton}>
              <CustomText useTranslationText={false} text="Status:" size="xs" style={styles.statusLabel} />
              <CustomText useTranslationText={false} text="Open" size="xs" weight="medium" />
              <CaretDown size={14} color={colors.grey[500]} />
            </Pressable> */}
          </View>
        </View>

        {/* Student Header Section */}
        {/* Body */}
        <View style={styles.body}>
          <View style={styles.studentSection}>
            <View style={styles.studentRow}>
              {/* Initials Circle */}
              <View style={styles.initialsCircle}>
                <CustomText
                  text={getInitials(selectedCase.studentName)}
                  weight="bold"
                  size="s"
                  useTranslationText={false}
                  style={{ color: colors.brand[600] }}
                />
              </View>

              {/* Name + Email */}
              <View style={styles.studentInfo}>
                <CustomText
                  text={selectedCase.studentName}
                  weight="medium"
                  useTranslationText={false}
                  style={{ color: colors.grey[900] }}
                />

                <View style={styles.studentMetaRow}>
                  <CustomText
                    text={selectedCase.studentEmail}
                    size="xs"
                    useTranslationText={false}
                    style={{ color: colors.grey[500] }}
                  />
                  {/* Do not remove it will be implemented in the next PR */}
                  {/* Optional Course Example */}
                  {/* <CustomText text="•" size="xs" style={{ color: colors.grey[400] }} /> */}
                  {/* <CustomText text="BIO-101" size="xs" useTranslationText={false} style={{ color: colors.grey[500] }} /> */}
                </View>
              </View>
            </View>
          </View>

          <View style={{ marginTop: 16 }}>
            <View style={styles.tabsContainer}>
              <View style={styles.tabsRow}>
                {/* Case Details Tab */}
                <Pressable
                  style={[styles.tabButton, activeTab === SafetyTab.DETAILS && styles.activeTab]}
                  onPress={() => setActiveTab(SafetyTab.DETAILS)}>
                  <ShieldAlert
                    size={16}
                    color={activeTab === SafetyTab.DETAILS ? colors.brand[600] : colors.grey[500]}
                  />
                  <CustomText
                    text="Case Details"
                    useTranslationText={false}
                    size="s"
                    weight="medium"
                    style={{
                      color: activeTab === SafetyTab.DETAILS ? colors.brand[600] : colors.grey[600],
                    }}
                  />
                </Pressable>

                {/* Do not remove it will be implemented in the next PR */}
                {/* Activity Tab */}
                {/* <Pressable
                  style={[styles.tabButton, activeTab === SafetyTab.ACTIVITY && styles.activeTab]}
                  onPress={() => setActiveTab(SafetyTab.ACTIVITY)}>
                  <Clock size={16} color={activeTab === SafetyTab.ACTIVITY ? colors.brand[600] : colors.grey[500]} />
                  <CustomText
                    useTranslationText={false}
                    text="Activity & Notes"
                    size="s"
                    weight="medium"
                    style={{
                      color: activeTab === SafetyTab.ACTIVITY ? colors.brand[600] : colors.grey[600],
                    }}
                  />

                  {/* Badge */}
                {/* <View style={styles.tabBadge}>
                    <CustomText useTranslationText={false} text="1" size="xs" weight="bold" />
                  </View>
                </Pressable>  */}
              </View>
            </View>
          </View>

          <View style={styles.tabContent}>
            {activeTab === SafetyTab.DETAILS && (
              <View style={styles.caseCard}>
                <CustomText
                  text={selectedCase.caseDetails || ""}
                  size="s"
                  useTranslationText={false}
                  style={{
                    fontSize: 14,
                    lineHeight: 20,
                    color: "rgb(16, 24, 40)",
                  }}
                />

                <View style={styles.caseFooter}>
                  <Text style={{ color: colors.grey[500], fontSize: 12 }}>{formatDate(selectedCase.time)}</Text>

                  {/* Do not remove it will be implemented in the next PR */}
                  {/* <Pressable style={styles.transcriptButton}>
                    <FileText size={14} color={colors.brand[600]} />
                    <CustomText
                      useTranslationText={false}
                      text="Request Full Transcript"
                      size="xs"
                      weight="medium"
                      style={{ color: colors.brand[600] }}
                    />
                  </Pressable> */}
                </View>
              </View>
            )}
            {/* Do not remove it will be implemented in the next PR */}
            {/* {activeTab === SafetyTab.ACTIVITY && (
              <View style={{ padding: 16 }}>
                <CustomText useTranslationText={false} text="Activity & Notes content goes here" />
              </View>
            )} */}
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          {/* Do not remove it will be implemented in the next PR */}
          {/* <CustomButton text="Message Student" styleType="primary" />
          <CustomButton text="Mark as Resolved" styleType="lightStroke" /> */}
        </View>
      </Animated.View>
    </>,
    document.body
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "rgba(0,0,0,0.4)",
    zIndex: 1000,
  },
  studentSection: {
    width: "100%",
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
  },
  studentRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  initialsCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.brand[100],
    alignItems: "center",
    justifyContent: "center",
  },
  studentInfo: {
    flex: 1,
  },

  studentMetaRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginTop: 2,
  },

  tabsContainer: {
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },

  tabsRow: {
    flexDirection: "row",
    gap: 8,
  },

  tabButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },

  activeTab: {
    borderBottomColor: colors.brand[600],
  },

  tabBadge: {
    marginLeft: 4,
    backgroundColor: colors.grey[100],
    borderRadius: 999,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },

  tabContent: {
    paddingVertical: 16,
  },

  caseCard: {
    borderWidth: 1,
    borderColor: colors.error[200],
    backgroundColor: colors.error[50],
    borderRadius: 8,
    padding: 16,
  },

  caseFooter: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },

  transcriptButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  panel: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: 550,
    backgroundColor: "#FFFFFF",
    zIndex: 1001,
    padding: 24,
    borderLeftWidth: 1,
    borderLeftColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOpacity: 0.2,
    shadowRadius: 8,
  },
  body: {
    flex: 1,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 12,
  },
  caseHeader: {
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },

  caseHeaderTopRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },

  caseMetaRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 4,
  },

  caseId: {
    fontFamily: "monospace",
    color: colors.grey[500],
  },

  caseTitle: {
    color: colors.grey[900],
  },

  moreButton: {
    padding: 8,
    borderRadius: 8,
  },

  caseActionsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginTop: spacing.sm,
  },

  severityBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: colors.error[50],
  },

  statusButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    height: 32,
    paddingHorizontal: 12,
    borderRadius: 6,
    backgroundColor: colors.grey[100],
  },

  statusLabel: {
    color: colors.grey[500],
  },
});
